import React, { useMemo } from 'react';
import { usePhases } from '../../store/configStore';
import PageBlockRenderer from './PageBlockRenderer';
import { useRegisterFocusMode, FocusModePriority } from '../../store/focusModeStore';

/**
 * Shared custom hook to register focus mode configuration across rotation renderers.
 */
const useRegisterRotationFocusMode = (slotData, isTopLevelAlone) => {
    const isObject = slotData && !Array.isArray(slotData);
    const overwriteFocusMode = isObject ? Boolean(slotData.overwriteFocusMode) : Boolean(slotData?.overwriteFocusMode);
    const isFocusModeDesktop = isObject ? Boolean(slotData.isFocusModeDesktop) : Boolean(slotData?.isFocusModeDesktop);
    const isFocusModeMobile = isObject ? Boolean(slotData.isFocusModeMobile) : Boolean(slotData?.isFocusModeMobile);

    useRegisterFocusMode(
        'slot',
        FocusModePriority.SLOT,
        isFocusModeDesktop,
        isFocusModeMobile,
        Boolean(slotData && isTopLevelAlone && overwriteFocusMode)
    );
};

export const PhaseRotationRenderer = ({ config, isTopLevelAlone }) => {
    const votingPhases = usePhases();
    const { slots } = config || {};

    const appStatusKey = (votingPhases?.appStatus || 'SIGNUP').toLowerCase();
    const slotData = slots?.[appStatusKey];
    const isObject = slotData && !Array.isArray(slotData);
    const body = isObject ? slotData.body : slotData;

    useRegisterRotationFocusMode(slotData, isTopLevelAlone);

    if (!slots) return null;

    if (!Array.isArray(body) || body.length === 0) {
        return null;
    }

    return <PageBlockRenderer blocks={body} />;
};

export const RoundRobinRotationRenderer = ({ config, isTopLevelAlone }) => {
    const { startDate, durationSeconds, slots } = config || {};

    const startMs = startDate ? Date.parse(startDate) : NaN;
    const hasValidDuration = !isNaN(startMs) && durationSeconds && durationSeconds > 0;

    let activeSlot = null;
    if (slots && Array.isArray(slots) && slots.length > 0) {
        if (hasValidDuration) {
            const elapsedMs = Math.max(0, Date.now() - startMs);
            const slotIndex = Math.floor(elapsedMs / (durationSeconds * 1000)) % slots.length;
            activeSlot = slots[slotIndex];
        } else {
            activeSlot = slots[0];
        }
    }

    const body = activeSlot?.body;

    useRegisterRotationFocusMode(activeSlot, isTopLevelAlone);

    if (!slots || !Array.isArray(slots) || slots.length === 0) {
        return null;
    }

    if (!body || !Array.isArray(body) || body.length === 0) {
        return null;
    }

    return <PageBlockRenderer blocks={body} />;
};

export const WeightedRandomRotationRenderer = ({ config, isTopLevelAlone }) => {
    const selectedWeightedSlot = useMemo(() => {
        const slots = config?.slots;
        if (!slots || !Array.isArray(slots) || slots.length === 0) {
            return null;
        }

        const totalWeight = slots.reduce((sum, slot) => sum + (Number(slot?.weight) || 0), 0);
        if (totalWeight <= 0) {
            return slots[0];
        }

        let random = Math.random() * totalWeight;
        for (const slot of slots) {
            const weight = Number(slot?.weight) || 0;
            if (random < weight) {
                return slot;
            }
            random -= weight;
        }

        return slots[slots.length - 1];
    }, [config]);

    const body = selectedWeightedSlot?.body;

    useRegisterRotationFocusMode(selectedWeightedSlot, isTopLevelAlone);

    if (!body || !Array.isArray(body) || body.length === 0) {
        return null;
    }

    return <PageBlockRenderer blocks={body} />;
};

const RotationRenderer = ({ node, isTopLevelAlone, ...restProps }) => {
    const props = node?.props || restProps;
    const { rotationKind, config, phaseConfig, roundRobinConfig, weightedRandomConfig } = props;

    let activeConfig = config;
    if (!activeConfig) {
        if (rotationKind === 'phase') {
            activeConfig = phaseConfig;
        } else if (rotationKind === 'roundRobin') {
            activeConfig = roundRobinConfig;
        } else if (rotationKind === 'weightedRandom') {
            activeConfig = weightedRandomConfig;
        }
    }

    switch (rotationKind) {
        case 'phase':
            return <PhaseRotationRenderer config={activeConfig} isTopLevelAlone={isTopLevelAlone} />;
        case 'roundRobin':
            return <RoundRobinRotationRenderer config={activeConfig} isTopLevelAlone={isTopLevelAlone} />;
        case 'weightedRandom':
            return <WeightedRandomRotationRenderer config={activeConfig} isTopLevelAlone={isTopLevelAlone} />;
        default:
            return null;
    }
};

export default RotationRenderer;
